import { ChangeDetectionStrategy, Component, computed, Inject, inject, OnInit, signal, TemplateRef, viewChild, } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectChange, MatSelectModule } from '@angular/material/select';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { SharedModels } from '@share-models';
import { features } from '@consts/text.const';
import { PreloaderColor } from '@enums/shared.enum';
import { AppState } from '../../../../../../store/app.state';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { CellTemplateContext, DatasetTableConfig, UiDatasetTableComponent, } from '@ui/lib/ui-kit/ui-dataset-table';
import { EnableEscCloseDirective, UiButtonComponent, UiModels, UiNoDataComponent } from '@ui';
import { UiPreloaderComponent } from '../../../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiSearchComponent } from '../../../../../../shared/ui-kit/ui-search/ui-search.component';
import { createSharedWithTableConfig } from './shared-with-table.util';
import openDialogByParams = UiModels.openDialogByParams;
import { routerSegments } from '@consts/routes';
import { Router } from '@angular/router';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import { vehicleGroupsFeature } from '@states/vehicle-groups/vehicle-groups.feature';
import { VehicleGroupsActions } from '@states/vehicle-groups/vehicle-groups.actions';

@Component({
  selector: 'app-vehicle-share-group-dialog',
  templateUrl: './share-vehicle-group-dialog.component.html',
  styleUrl: './share-vehicle-group-dialog.component.scss',
  imports: [
    TranslatePipe,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltip,
    UiButtonComponent,
    UiDatasetTableComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiSearchComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class ShareVehicleGroupDialogComponent implements OnInit {
  protected readonly VehicleGroupsModel = VehicleGroupsModel;
  protected readonly loaderColor = PreloaderColor;
  protected readonly viewStates = SharedModels.ViewState;

  protected readonly shareDialogInitialEmpty = features.vehicleGroups.shareDialog.initialEmpty;
  protected readonly shareDialogNoResults = features.vehicleGroups.shareDialog.noResults;

  protected readonly actionsCellTemplate = viewChild.required<
    TemplateRef<CellTemplateContext<VehicleGroupsModel.VehicleGroupSharedWithRow>>
  >('actionsCell');

  private readonly store$ = inject(Store<AppState>);
  private readonly router = inject(Router);
  private readonly clipboard = inject(Clipboard);
  private readonly dialogRef = inject(MatDialogRef<ShareVehicleGroupDialogComponent>);
  private readonly activeOrgId = this.store$.selectSignal(OrganizationSelectors.selectOrgId);

  protected readonly memberSearch = signal('');
  protected readonly selectedAccessRole = signal<VehicleGroupsModel.ShareAccessRole | null>(null);
  private readonly shareOptions = this.store$.selectSignal(vehicleGroupsFeature.selectShareOptions);

  protected readonly vehicleGroupAccessRoles = computed(() => VehicleGroupsModel.SHARE_ACCESS_ROLES);
  protected readonly availableInviteRoles = computed(() =>
    VehicleGroupsModel.invitableShareRolesForCaller(this.myShareAccessRole()),
  );
  protected readonly vehicleGroupInviteCode = computed(() => this.shareOptions().inviteCode);
  protected readonly isInviteCodeLoading = computed(() => this.shareOptions().inviteCodeLoading);
  protected readonly vehicleGroupAccessList = computed(() => this.shareOptions().accessList);
  protected readonly vehicleGroupShareOptionsLoading = computed(() => {
    const so = this.shareOptions();
    return so.accessListLoading || (so.inviteCodeLoading && so.inviteCode == null);
  });

  protected readonly filteredSharedWithOrgs = computed(() => {
    const q = this.memberSearch().trim().toLowerCase();
    const rows = this.vehicleGroupAccessList();
    if (!q) {
      return rows;
    }
    return rows.filter((r) => r.orgName.toLowerCase().includes(q));
  });

  protected readonly accessTypeSelectValue = computed(() => {
    const invitable = this.availableInviteRoles();
    const selected = this.selectedAccessRole();
    if (selected != null && invitable.includes(selected)) {
      return selected;
    }
    return invitable[0] ?? null;
  });

  protected readonly tableConfig = computed<DatasetTableConfig<VehicleGroupsModel.VehicleGroupSharedWithRow>>(() =>
    createSharedWithTableConfig({ actionsCell: this.actionsCellTemplate() }),
  );

  protected readonly viewState = computed(() => {
    const rows = this.filteredSharedWithOrgs();
    if (rows.length > 0) {
      return SharedModels.ViewState.HAS_CONTENT;
    }
    return this.hasActiveOrgSearch
      ? SharedModels.ViewState.NO_RESULTS
      : SharedModels.ViewState.INITIAL_EMPTY;
  });

  protected get hasActiveOrgSearch(): boolean {
    return this.memberSearch().trim().length > 0;
  }

  protected isUpdatingAccessForOrg(orgId: string): boolean {
    return this.shareOptions().updatingAccessOrgIds.includes(orgId);
  }

  protected readonly myShareAccessRole = computed((): VehicleGroupsModel.ShareAccessRole | null => {
    const active = this.activeOrgId();
    const createdByOrgId = this.shareOptions().createdByOrgId ?? this.data.createdByOrgId;
    return VehicleGroupsModel.resolveMyShareAccessRoleFromSharedWithRows(
      this.vehicleGroupAccessList(),
      createdByOrgId,
      active,
    );
  });

  protected readonly isShareEditingLocked = computed(() => this.availableInviteRoles().length === 0);

  protected isShareEditingLockedForOrg(row: VehicleGroupsModel.VehicleGroupSharedWithRow): boolean {
    const active = this.activeOrgId();
    if (active == null || active === '') {
      return true;
    }
    if (row.orgId === this.data.createdByOrgId) {
      return active !== row.orgId;
    }
    const myRole = this.myShareAccessRole();
    return myRole == null || myRole !== VehicleGroupsModel.ShareAccessRole.Admin;
  }

  public constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: VehicleGroupsModel.ShareGroupDialogData,
  ) {}

  public ngOnInit(): void {
    this.store$.dispatch(
      VehicleGroupsActions.startLoadingVehicleGroupShareOptions({ groupId: this.data.groupId }),
    );
  }

  protected onClose(): void {
    this.dialogRef.close();
  }

  protected onAccessTypeChange(event: MatSelectChange): void {
    if (this.isShareEditingLocked()) {
      return;
    }
    const value = event.value as string;
    if (!VehicleGroupsModel.isShareAccessRole(value)) {
      return;
    }
    if (!this.availableInviteRoles().includes(value)) {
      return;
    }
    this.selectedAccessRole.set(value);
    this.store$.dispatch(
      VehicleGroupsActions.setVehicleGroupInviteCodeLoading({ vehicleGroupInviteCodeLoading: true }),
    );
    this.store$.dispatch(
      VehicleGroupsActions.loadVehicleGroupInviteCode({
        groupId: this.data.groupId,
        accessType: value,
      }),
    );
  }

  protected onAccessListItemChange(
    row: VehicleGroupsModel.VehicleGroupSharedWithRow,
    event: MatSelectChange,
  ): void {
    if (this.isShareEditingLockedForOrg(row)) {
      return;
    }
    const value = event.value as string;
    if (!VehicleGroupsModel.isShareAccessRole(value)) {
      return;
    }
    if (!this.availableInviteRoles().includes(value)) {
      return;
    }
    this.store$.dispatch(
      VehicleGroupsActions.updateVehicleGroupAccessListItem({
        groupId: this.data.groupId,
        orgId: row.orgId,
        accessType: value,
      }),
    );
  }

  protected removeOrgFromOption(
    row: VehicleGroupsModel.VehicleGroupSharedWithRow,
    rowRoleSelect: MatSelect,
    event: MouseEvent | KeyboardEvent,
  ): void {
    if (this.isShareEditingLockedForOrg(row)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.store$.dispatch(
      VehicleGroupsActions.requestRemoveVehicleGroupAccess({
        groupId: this.data.groupId,
        orgId: row.orgId,
        orgName: row.orgName,
      }),
    );
    rowRoleSelect.close();
  }

  protected async shareCode(): Promise<void> {
    const code = this.vehicleGroupInviteCode();
    if (!code || this.isInviteCodeLoading()) {
      return;
    }
    const url = this.buildShareUrl(code);
    if (typeof navigator === 'undefined' || !navigator.share) {
      this.clipboard.copy(url);
      return;
    }
    try {
      await navigator.share({ url });
    } catch {
      // User dismissed share sheet or share failed.
    }
  }

  protected copyInviteCode(): void {
    const code = this.vehicleGroupInviteCode();
    if (!code || this.isInviteCodeLoading()) {
      return;
    }
    this.clipboard.copy(code);
  }

  private buildShareUrl(code: string): string {
    const tree = this.router.createUrlTree(
      ['/', routerSegments.database, routerSegments.vehicleGroups],
      {
        queryParams: {
          [openDialogByParams.joinVehicleGroup]: 'true',
          [VehicleGroupsModel.JoinRouteQueryParam.InviteCode]: code,
        },
      },
    );
    const pathWithQuery = this.router.serializeUrl(tree);
    return (
      typeof window !== 'undefined' && window.location?.origin
        ? `${window.location.origin}${pathWithQuery}`
        : pathWithQuery
    );
  }
}
